import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Switch } from '../../components/ui/Switch';

export const SCHEMA_FIELD_TYPES = [
  'string',
  'text',
  'integer',
  'decimal',
  'float',
  'boolean',
  'date',
  'datetime',
  'email',
  'url',
  'uuid',
  'json',
  'enum',
  'array'
];

export const emptySchemaField = () => ({
  slug: '',
  name: '',
  type: 'string',
  required: false,
  unique: false,
  indexed: false,
  enumText: ''
});

export const fieldsFromEntity = (fields) => {
  if (!Array.isArray(fields) || !fields.length) {
    return [emptySchemaField()];
  }

  return fields.map((field) => {
    const slug = field.slug || field.Slug || '';
    const type = field.type || field.Type || 'string';
    const enumValues = field.enum || field.Enum;

    return {
      slug,
      name: field.name || field.Name || slug,
      type: SCHEMA_FIELD_TYPES.includes(type) ? type : 'string',
      required: Boolean(field.required ?? field.Required),
      unique: Boolean(field.unique ?? field.Unique),
      indexed: Boolean(field.indexed ?? field.Indexed),
      enumText: Array.isArray(enumValues) ? enumValues.join(', ') : ''
    };
  });
};

export const serializeSchemaFields = (fields) =>
  fields
    .map((field) => {
      const slug = field.slug.trim();
      const name = field.name.trim() || slug;
      if (!slug) {
        return null;
      }

      const payload = {
        slug,
        name,
        type: field.type || 'string',
        required: Boolean(field.required),
        unique: Boolean(field.unique),
        indexed: Boolean(field.indexed)
      };

      if (payload.type === 'enum') {
        payload.enum = field.enumText
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return payload;
    })
    .filter(Boolean);

export const EntitySchemaEditor = ({ fields, disabled = false, t, onChange }) => {
  const updateField = (index, patch) => {
    onChange(fields.map((field, fieldIndex) => (fieldIndex === index ? { ...field, ...patch } : field)));
  };

  return (
    <div className="space-y-3">
      {fields.map((field, index) => (
        <div
          key={`schema-field-${index}`}
          className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950"
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t('requestsFieldSlug')}
              value={field.slug}
              disabled={disabled}
              dir="ltr"
              className="rounded-xl px-3 py-2.5 text-left"
              onChange={(event) => updateField(index, { slug: event.target.value })}
            />
            <Input
              label={t('requestsFieldName')}
              value={field.name}
              disabled={disabled}
              className="rounded-xl px-3 py-2.5 text-start"
              onChange={(event) => updateField(index, { name: event.target.value })}
            />
            <label className="block space-y-2">
              <span className="text-sm font-medium text-muted">{t('requestsFieldType')}</span>
              <select
                className="w-full rounded-2xl border border-border bg-white/80 px-4 py-3 text-base text-text outline-none md:text-sm dark:bg-panel-strong"
                value={field.type}
                disabled={disabled}
                onChange={(event) => updateField(index, { type: event.target.value })}
              >
                {SCHEMA_FIELD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>
            {field.type === 'enum' ? (
              <Input
                label={t('requestsFieldEnum')}
                value={field.enumText}
                disabled={disabled}
                className="rounded-xl px-3 py-2.5 text-start"
                onChange={(event) => updateField(index, { enumText: event.target.value })}
              />
            ) : null}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
                <Switch
                  checked={field.required}
                  disabled={disabled}
                  onChange={(checked) => updateField(index, { required: checked })}
                  aria-label={t('requestsFieldRequired')}
                />
                {t('requestsFieldRequired')}
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
                <Switch
                  checked={field.unique}
                  disabled={disabled}
                  onChange={(checked) => updateField(index, { unique: checked })}
                  aria-label={t('requestsFieldUnique')}
                />
                {t('requestsFieldUnique')}
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
                <Switch
                  checked={field.indexed}
                  disabled={disabled}
                  onChange={(checked) => updateField(index, { indexed: checked })}
                  aria-label={t('requestsFieldIndexed')}
                />
                {t('requestsFieldIndexed')}
              </label>
            </div>
            <Button
              type="button"
              variant="secondary"
              className="h-10 px-3"
              disabled={disabled || fields.length <= 1}
              onClick={() => onChange(fields.filter((_, fieldIndex) => fieldIndex !== index))}
            >
              <Trash2 size={15} />
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        className="w-full justify-center"
        disabled={disabled}
        onClick={() => onChange([...fields, emptySchemaField()])}
      >
        <Plus size={16} />
        {t('requestsAddField')}
      </Button>
    </div>
  );
};
